import libphonenumber from 'google-libphonenumber';
import { html, unsafeCSS } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { DomUtility, SvgUtility } from '~/ui/utils';
import { LocaleCode } from '~/common/interfaces';

import { styles as globalStyles } from '~/ui/scss/main.styles';
import { styles } from './phone-input.styles';

import Dropdown from '~/ui/components/dropdown';
import Input from '~/ui/components/input';
import DERFormElement from '~/ui/utils/der-form-element';

export interface CountryCode {
    name: string;
    id: string;
}

export class PhoneInput extends DERFormElement {
    static override styles = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    @query('der-ui-dropdown') dropdownEl?: Dropdown;

    @query('der-ui-input') inputEl?: Input;

    @property({ type: String })
    componentId: string = DomUtility.uniqueElementId(this);

    @property({ type: String, attribute: 'label' })
    label?: string = 'Phone Number';

    @property({ type: String, attribute: 'optional-label' })
    optionalLabel? = '(Optional)';

    @property({ type: String, attribute: 'input-placeholder' })
    inputPlaceholder?: string;

    @property({ type: String })
    override lang = LocaleCode.DE_CH;

    @property({ type: Boolean, attribute: 'required' })
    required = false;

    @property({ type: String, attribute: 'phone-number' })
    phoneNumber: string = '';

    @property({ type: String })
    phonePattern = '^[\\+]?[0-9\\s\\-\\(\\)]{6,42}$';

    @property({ type: String })
    countryCode: string = '';

    @property({ type: String, attribute: 'name' })
    name: string = '';

    @property({ type: Boolean, attribute: 'show-tooltip' })
    showTooltip: boolean = false;

    @property({ type: String, attribute: 'validate-message' })
    validateMessage: string =
        this.lang === LocaleCode.DE_CH
            ? 'Bitte geben Sie eine gültige Telefonnummer ein.'
            : 'Veuillez entrer un numéro de téléphone valide.';

    private get countryCodes(): CountryCode[] {
        return [
            { name: 'CH (+41)', id: 'CH' },
            { name: 'DE (+49)', id: 'DE' },
            { name: 'LI (+423)', id: 'LI' },
            { name: 'FR (+33)', id: 'FR' },
            { name: 'AT (+43)', id: 'AT' },
            { name: 'IT (+39)', id: 'IT' },
            { name: this.lang === LocaleCode.DE_CH ? 'andere' : 'autre', id: '0' },
        ];
    }

    private get dropdownPlaceholder(): string {
        return this.lang === LocaleCode.DE_CH ? 'Bitte wählen' : 'Veuillez choisir';
    }

    /**
     * Constructor for the PhoneInput component.
     * Attaches an open shadow DOM with focus delegation enabled.
     */
    constructor() {
        super();
        this.attachShadow({
            mode: 'open',
            delegatesFocus: true,
        });
    }

    /**
     * Initializes the component by updating the form value, setting the input placeholder,
     * and checking the validity of the phone number when the component is connected to the DOM.
     */
    override connectedCallback() {
        super.connectedCallback();
        this.updateComplete.then(() => {
            this.updateFormValue();
            this.setInputPlaceholder();
            this.checkValidity();
        });
    }

    /**
     * updates the form value, placeholder and validity when phoneNumber or countryCode changes
     * @param changedProps
     */
    override updated(changedProps: Map<string, unknown>) {
        if (changedProps.has('phoneNumber') || changedProps.has('countryCode')) {
            this.updateFormValue();
            this.setInputPlaceholder();
            this.checkValidity();
        }
        // Update language-dependent properties when lang changes
        if (changedProps.has('lang')) {
            this.setInputPlaceholder();
            this.requestUpdate();
        }
    }

    /**
     * Sets the input placeholder based on the selected country code and language.
     */
    setInputPlaceholder() {
        this.inputPlaceholder = this.countryCode === '0' ? '+411234567890' : '1234567890';
    }

    /**
     * Updates the visible form value with the E164-formatted phone number.
     * Uses google-libphonenumber for formatting.
     * Sets an empty string if the number is too short.
     */
    private updateFormValue() {
        if (this.phoneNumber.length > 4) {
            let fullNumber = '';
            const PNF = libphonenumber.PhoneNumberFormat;
            const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

            // andere Vorwahl
            if (this.countryCode === '0') {
                const numberProto = phoneUtil.parse(this.phoneNumber, 'CH');
                fullNumber = phoneUtil.format(numberProto, PNF.E164) || '';
                // country code gewaehlt (+49, +41, etc.)
            } else if (this.countryCode && this.countryCode != '0' && this.phoneNumber) {
                const numberProto = phoneUtil.parse(this.phoneNumber, this.countryCode);
                fullNumber = phoneUtil.format(numberProto, PNF.E164);
            }

            this._internals.setFormValue(fullNumber);
        }
    }

    /**
     * Validates the entered phone number based on:
     * - Required field check when 'required' is true
     * - Pattern matching with the 'phonePattern' property
     *
     * Sets an E164-formatted value via the DERFormElement API
     * upon successful validation.
     */
    private checkValidity() {
        // First check if required fields are missing
        if (this.required && (!this.countryCode || !this.phoneNumber)) {
            this.setDERValidity({ valueMissing: true }, this.validateMessage);
            return;
        }

        // Now check if the phone number pattern is valid (if there is a phone number)
        if (this.phoneNumber && this.inputEl) {
            // Access the native input element inside our custom Input component
            const nativeInputEl = this.inputEl.inputEl;

            if (nativeInputEl && !nativeInputEl.validity.valid) {
                // If the phone input is invalid (due to pattern mismatch or other reasons)
                this.setDERValidity({ patternMismatch: true }, this.validateMessage);
                return;
            }
        }

        // If we get here, all validations passed
        this.setDERValidity({});
    }

    private _onInputValidityChange(event: Event) {
        event.preventDefault(); // Prevent default browser validation behavior
        event.stopPropagation();
        this.checkValidity();
        this.requestUpdate();
    }

    private _onCountryCodeChange(event: CustomEvent) {
        this.countryCode = event.detail.value;
    }

    private _onPhoneNumberInput(event: CustomEvent) {
        this.phoneNumber = event.detail.value;
        this.checkValidity();
    }

    override render() {
        const { required, label, optionalLabel } = this;
        const isInvalid = this.required && (!this.countryCode || !this.phoneNumber);

        return html`
            <div
                class=${classMap({
                    'c-phone-input': true,
                    'is-invalid': isInvalid,
                })}
            >
                <div class="c-phone-input__label-wrapper">
                    ${label ? html`<label class="c-phone-input__label">${label}</label>` : ''}
                    ${!required ? html`<span class="c-phone-input__optional-label">${optionalLabel}</span>` : ''}
                    ${this.showTooltip
                        ? html`<div class="c-phone-input__tooltip">
                              <span class="tooltip-text">Tooltip text</span>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 40 40">
                                  ${SvgUtility['systemInfoCircle']}
                              </svg>
                          </div>`
                        : ''}
                </div>

                <div class="c-phone-input__fields-wrapper">
                    <der-ui-dropdown
                        class="c-phone-input__dropdown"
                        name="country-code-${this.componentId}"
                        optional-label=""
                        combined="true"
                        placeholder=""
                        value="CH"
                        ?required="${this.required}"
                        .options="${this.countryCodes}"
                        @der.change="${this._onCountryCodeChange}"
                    ></der-ui-dropdown>
                    <der-ui-input
                        class="c-phone-input__number"
                        name="phone-number-${this.componentId}"
                        optional-label=""
                        combined="true"
                        type="tel"
                        inputmode="tel"
                        pattern="${this.phonePattern}"
                        placeholder="${this.inputPlaceholder}"
                        ?required="${this.required}"
                        @der.input="${this._onPhoneNumberInput}"
                        @der.validity-change="${this._onInputValidityChange}"
                    ></der-ui-input>
                </div>
            </div>
        `;
    }
}
